import {
  ReferralCodeInfoDocument,
  ReferralCodeInfoQuery,
} from "@frec-js/common-client";
import { GetServerSideProps, NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect } from "react";

import { createApolloClient } from "../../utils/ssrApollo";

// We need to SSR the meta tags for this page, so we're using a clientside
// redirect to the signup page (which doesn't have SSR support yet)
type ReferralCodeProps = {
  invitedBy: string | null;
  referralCode: string | "unknown";
};

const ReferralCode: NextPage<ReferralCodeProps> = ({
  invitedBy,
  referralCode,
}) => {
  const title = invitedBy
    ? `${invitedBy} invited you to join Frec`
    : `You have been invited to join Frec`;

  const router = useRouter();

  useEffect(() => {
    router.replace(
      /**
       * Note: this does not quite work for localhost since frec-web and
       * brokerage web run on different hosts (localhost:3000 vs localhost:3001)
       */
      `/app/signup?referralCode=${referralCode}`,
    );
  }, [referralCode, router]);

  return (
    <>
      <Head>
        <title>{title}</title>
        {referralCode && (
          <link
            rel="canonical"
            href={
              /**
               * Note: this does not quite work for localhost since frec-web and
               * brokerage web run on different hosts (localhost:3000 vs localhost:3001)
               */
              `/referral/${referralCode}`
            }
          />
        )}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        {/* don't index this page */}
        <meta name="robots" content="noindex, nofollow" />{" "}
        <meta
          property="og:image"
          content="https://frec.com/webstatic/images/referral-img.png"
          key="og:image"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Head>
      <>{/* empty page */}</>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  ReferralCodeProps
> = async ({ res, params }) => {
  const referralCode = params?.referralCode as string | undefined;
  const client = createApolloClient();
  const { data } = await client.query<ReferralCodeInfoQuery>({
    query: ReferralCodeInfoDocument,
    variables: { code: referralCode },
  });
  const invitedBy = referralCode && data?.referralCodeInfo?.invitedBy;
  if (!referralCode) {
    res.statusCode = 404;
  }
  const props: ReferralCodeProps = invitedBy
    ? { referralCode, invitedBy }
    : referralCode
      ? { referralCode, invitedBy: null }
      : { referralCode: "unknown", invitedBy: null };

  return Promise.resolve({
    props,
  });
};

export default ReferralCode;
